import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

const safeText = (value) => {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch (error) {
    return String(value);
  }
};

const isPngDataUrl = (value) => typeof value === 'string' && value.startsWith('data:image/png');

const exportPdf = async (...args) => {
  const params =
    typeof args[0] === 'string'
      ? {
          title: args[0],
          rows: args[1],
          columns: args[2],
          theme: args[3],
          t: args[4],
          branding: args[5] || {},
        }
      : args[0] || {};

  const {
    title,
    rows = [],
    columns = [],
    theme,
    t,
    branding = {},
  } = params;

  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const primary = theme?.palette?.primary?.main || '#1976d2';
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const header = branding.header || {};
  const footer = branding.footer ? safeText(branding.footer) : '';
  const logoUrl = header.logoUrl;

  const topMargin = 70;
  let cursorY = topMargin;

  if (logoUrl && isPngDataUrl(logoUrl)) {
    try {
      const maxLogoWidth = Math.min(pageWidth * 0.35, 200);
      const logoHeight = maxLogoWidth * 0.35;
      doc.addImage(logoUrl, 'PNG', (pageWidth - maxLogoWidth) / 2, topMargin - logoHeight - 12, maxLogoWidth, logoHeight);
    } catch (error) {
      console.warn('exportPdf: falha ao desenhar logomarca', error);
    }
  }

  const centerX = pageWidth / 2;
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(safeText(header.title || title), centerX, cursorY, { align: 'center' });
  cursorY += 18;

  doc.setFont('helvetica', 'normal');
  if (header.subtitle) {
    doc.setFontSize(11);
    doc.text(safeText(header.subtitle), centerX, cursorY, { align: 'center' });
    cursorY += 16;
  }

  if (header.generatedOn) {
    doc.setFontSize(10);
    doc.text(safeText(header.generatedOn), centerX, cursorY, { align: 'center' });
    cursorY += 14;
  }

  cursorY += 12;

  if (branding.watermark?.image && isPngDataUrl(branding.watermark.image)) {
    if (doc.saveGraphicsState && doc.restoreGraphicsState && doc.GState) {
      try {
        const wmWidth = pageWidth * 0.4;
        const wmHeight = wmWidth * 0.6;
        doc.saveGraphicsState();
        doc.setGState(new doc.GState({ opacity: branding.watermark.opacity ?? 0.06 }));
        doc.addImage(
          branding.watermark.image,
          'PNG',
          (pageWidth - wmWidth) / 2,
          (pageHeight - wmHeight) / 2,
          wmWidth,
          wmHeight
        );
        doc.restoreGraphicsState();
      } catch (error) {
        console.warn('exportPdf: falha ao aplicar watermark', error);
      }
    }
  }

  const headers = [columns.map((c) => c.headerName || c.field)];
  const body = rows.map((row) =>
    columns.map((col) => {
      const key = col.headerName || col.field;
      return safeText(row[key]);
    })
  );

  doc.autoTable({
    startY: cursorY,
    head: headers,
    body,
    theme: 'grid',
    styles: { fontSize: 9, cellPadding: 4, lineColor: [230, 230, 230] },
    headStyles: {
      fillColor: hexToRgb(primary),
      textColor: [255, 255, 255],
      fontStyle: 'bold',
    },
    alternateRowStyles: { fillColor: [250, 250, 250] },
    didDrawPage: () => {
      doc.setFontSize(9);
      doc.setTextColor(100);
      if (footer) {
        doc.text(footer, 40, doc.internal.pageSize.getHeight() - 25);
      }
      const pageNumber = doc.internal.getNumberOfPages();
      const pageLabel = safeText(t ? t('reportPageLabel', { page: pageNumber }) : `Page ${pageNumber}`);
      doc.text(pageLabel, pageWidth - 100, doc.internal.pageSize.getHeight() - 25);
    },
  });

  doc.save(`${title}.pdf`);
};

function hexToRgb(hex) {
  const clean = (hex || '#1976d2').replace('#', '');
  const bigint = parseInt(clean, 16);
  return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
}

export default exportPdf;
